/**
 * Compute the set of object IDs that should translate together when the user
 * moves `rootId`.
 *
 * The "move group" is the closure of {rootId} under three relations:
 *   1. Tree descent via parents[].id (or legacy parentId) — child-to-parent.
 *   2. Tree descent via children[] — parent-to-child.
 *   3. Equivalence via fixtureGroupId (used for fixture assemblies like a
 *      shower with its showerhead, controls, niche, etc.).
 *
 * The result is symmetric across fixture-group siblings: selecting any member
 * of an assembly returns the same set, so moving the controls drags the
 * shower exactly as moving the shower drags the controls.
 *
 * Pure function — does not mutate `objects`.
 *
 * `getMoveGroup` is MEMBERSHIP — what the group editor and the link solver
 * mean by "this group". What a FREE MOVE may translate is a narrower
 * question, because a cabinet run owns its own geometry: see
 * `utils/freeMoveGroup.js`, which walks the same closure through `blockedBy`.
 */

/**
 * The shared breadth-first walk. `blockedBy(obj)` — when given — marks an
 * object the closure may neither include NOR pass through: if it does not
 * move, nothing reachable only through it may move either.
 *
 * @param {string} rootId
 * @param {Object[]} objects
 * @param {(obj: Object) => boolean} [blockedBy]
 * @returns {Set<string>}
 */
function walkMoveGroup(rootId, objects, blockedBy) {
  if (!rootId || !Array.isArray(objects)) return new Set();

  const group = new Set([rootId]);
  const queue = [rootId];
  // The root always travels — it is the object the user grabbed.
  const admits = (obj) => !obj || !blockedBy || !blockedBy(obj);

  while (queue.length > 0) {
    const id = queue.shift();
    const me = objects.find((o) => o.id === id);

    // Child-to-parent: find objects whose parents[] include the current id
    for (const obj of objects) {
      if (group.has(obj.id)) continue;
      const isChild =
        obj.parents?.some((p) => p.id === id) || obj.parentId === id;
      if (isChild && admits(obj)) {
        group.add(obj.id);
        queue.push(obj.id);
      }
    }

    // Parent-to-child: follow the children[] array
    if (me?.children) {
      for (const childId of me.children) {
        if (group.has(childId)) continue;
        const child = objects.find((o) => o.id === childId);
        if (child && admits(child)) {
          group.add(childId);
          queue.push(childId);
        }
      }
    }

    if (me?.fixtureGroupId) {
      for (const sib of objects) {
        if (group.has(sib.id)) continue;
        if (sib.fixtureGroupId === me.fixtureGroupId && admits(sib)) {
          group.add(sib.id);
          queue.push(sib.id);
        }
      }
    }
  }

  return group;
}

export function getMoveGroup(rootId, objects) {
  return walkMoveGroup(rootId, objects);
}

export { walkMoveGroup };
